import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { FormItemProps } from "antd/lib";
import { ReactElement } from "react";

import {
  CalculationResult,
  MediumEnum,
  PackageChoiceEnum,
  SubOrderRequest,
} from "../../../api";
import { subOrderGenericFieldsFilled } from "../../../utils";
import { OnChangeSubOrder } from "../../campaigns/models";
import { useCampaign } from "../../providers/CampaignProvider";
import BonusInput from "./partials/BonusInput";
import BudgetInput from "./partials/BudgetInput";
import CalculateGrpBudgetWrapper from "./partials/CalculateGrpBudgetWrapper";
import { EditableColumnInfo } from "./partials/models";
import useBonusValidationGenerator from "./partials/useBonusValidationGenerator";
import useBudgetValidationGenerator from "./partials/useBudgetValidationGenerator";
import useFixedCostsSubOrderSettings from "./useFixedCostsSubOrderSettings";
import useGrpSubOrderSettings from "./useGrpSubOrderSettings";

export type EditableColumnName = "budget" | "grp" | "bonus" | "netto";

type EditableColumn = {
  editable?: (record: SubOrderRequest) => boolean;
  renderEditor: (record: SubOrderRequest) => ReactElement;
  onFormItem?: (record: SubOrderRequest) => FormItemProps<SubOrderRequest>;
};

type EditableColumnsMapValue = Partial<
  Record<EditableColumnName, EditableColumn>
>;
type EditableColumnsMap = Partial<
  Record<PackageChoiceEnum, EditableColumnsMapValue>
>;

export const useEditableColumns = ({
  medium,
  productId,
  requestDate,
  onChange,
}: {
  productId: number;
  medium: MediumEnum;
  onChange: OnChangeSubOrder;
  requestDate?: Date;
}) => {
  const i18n = useLingui();

  const { orderRequest } = useCampaign();

  const budgetValidationGenerator = useBudgetValidationGenerator();
  const bonusValidationGenerator = useBonusValidationGenerator();

  // Fixed costs settings and validation constraints
  const subOrderContexts = {
    fixedCosts: useFixedCostsSubOrderSettings(),
    grp: useGrpSubOrderSettings(),
  };

  // render method for grp sub order and online sub order
  const renderCalculateGrpBudget = (
    dataIndex: keyof SubOrderRequest,
    record: SubOrderRequest
  ) => (
    <CalculateGrpBudgetWrapper
      dataIndex={dataIndex}
      medium={medium}
      productId={productId}
      requestDate={requestDate}
      record={record}
      onCalculated={(values: CalculationResult) =>
        onChange({ id: record.id, ...values })
      }
    />
  );

  // construct map
  const onlineValue: EditableColumnsMapValue = {
    budget: {
      editable: (record) => !!record.period && !!record._package?.code, // could maybe move to subOrderGenericFieldsFilled and make dependent on PackageChoiceEnum
      renderEditor: (record) => renderCalculateGrpBudget("budget", record),
    },
  };

  const packageChoiceToEditableColumnMap: EditableColumnsMap = {
    [PackageChoiceEnum.Grps]: {
      grp: {
        editable: (record) =>
          subOrderGenericFieldsFilled(record) &&
          record._package?.isYourTime !== true,
        onFormItem: () => ({
          rules: [
            {
              required: true,
              message: i18n._(t`Voer het gewenste aantal GRP's in`),
            },
          ],
        }),
        renderEditor: (record) => renderCalculateGrpBudget("grp", record),
      },
      budget: {
        editable: (record) => subOrderGenericFieldsFilled(record),
        onFormItem: () => ({
          rules: budgetValidationGenerator.createWithConfig(
            subOrderContexts.grp.createInstanceSettings().validationConfigs
              .budget
          ),
        }),
        renderEditor: (record) => renderCalculateGrpBudget("budget", record),
      },
    },
    [PackageChoiceEnum.FixedCosts]: {
      budget: {
        editable: () => subOrderContexts.fixedCosts.isEditableBudget,
        onFormItem: (record) => ({
          rules: budgetValidationGenerator.createWithConfig(
            subOrderContexts.fixedCosts.createInstanceSettings(record)
              .validationConfigs.budget
          ),
        }),
        renderEditor: (record) => (
          <BudgetInput
            onChange={(budget) => {
              // Bonus follows budget when it previously was larger than the budget
              const newBonus = Math.min(record.bonus ?? 0, budget);
              onChange({ id: record.id, budget, bonus: newBonus });
            }}
          />
        ),
      },
      bonus: {
        editable: () => true,
        onFormItem: (record) => ({
          rules: bonusValidationGenerator.createWithConfig(
            subOrderContexts.fixedCosts.createInstanceSettings(record)
              .validationConfigs.bonus
          ),

          /* Update error message when budget is changed */
          dependencies: ["budget"],
        }),
        renderEditor: ({ budget, id }) => (
          <BonusInput
            disabled={!!orderRequest?.bonusConfiguration?.bonusOnOrderRequest}
            budget={budget ?? 0}
            onChange={(bonus) => onChange({ id, bonus })}
          />
        ),
      },
      netto: {
        editable: () => true,
        onFormItem:
          undefined /* onFormItem is not used - we rely on first bonus column to display 'issues' */,
        renderEditor: ({ budget, id }) => (
          <BonusInput
            disabled={!!orderRequest?.bonusConfiguration?.bonusOnOrderRequest}
            asNetto
            budget={budget ?? 0}
            onChange={(bonus) => onChange({ id, bonus })}
          />
        ),
      },
    },
    [PackageChoiceEnum.Display]: onlineValue,
    [PackageChoiceEnum.Video]: onlineValue,
  };

  const getFromMap = (
    record: SubOrderRequest,
    editableColumn: EditableColumnName
  ) => {
    if (record.packageChoice === undefined) {
      return undefined;
    }

    return packageChoiceToEditableColumnMap[record.packageChoice]?.[
      editableColumn
    ];
  };

  const resolveEditableColumn = (
    editableColumn: EditableColumnName
  ): EditableColumnInfo => ({
    editableCell: (record) =>
      getFromMap(record, editableColumn)?.editable?.(record) ?? false,
    onFormItem: (record) =>
      getFromMap(record, editableColumn)?.onFormItem?.(record) ?? {},
    renderEditor: (record) =>
      getFromMap(record, editableColumn)?.renderEditor(record) ?? <span />,
  });

  return {
    resolveEditableColumn,
  };
};
